import * as React from 'react';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import {
  CmdLabel,
  CmdModal,
  CmdModalFooterButton,
  CmdSimpleSelect,
  CmdTextarea,
} from '@commander-services/gui-components';
import { Controller, useForm } from 'react-hook-form';
import ApiService from '../../Services/ApiService';
import PermissionsService, { PERMISSION_IMPORTED_RECORD } from '../../Services/PermissionsService';
import TableService, { TABLE_REFUELINGS_IMPORT_RECORDS } from '../../Services/TableService';
import { IOption } from '../Forms/CmdField';
import { IFilesImportInput } from '.';
import usePageWithForm from '../../hooks/usePageWithForm';
import { transformErrors, validateFields } from '../../Services/HookFormService';

const RESPONSE_OK = 'ok';

interface ModalFormProps {
  handleClose: () => void;
  data: IFilesImportInput[];
  id: number;
  customerId: number | null;
  updateTable: () => void;
}

const IS_REFUELING = 'refueling';
const IS_EXPENSE = 'expense';

type EditFormValues = {
  status: string;
  errors: string;
  accountingTs: string;
  customerId: string;
  identifier: string;
  vehicleId: string;
  note: string;
  rideId: string;
  expenseTypeId: string;
};
type FormType =
  | 'status'
  | 'errors'
  | 'accountingTs'
  | 'customerId'
  | 'identifier'
  | 'vehicleId'
  | 'note'
  | 'rideId'
  | 'expenseTypeId';

type FormValidation = {
  status: IValidationRule[];
  errors: IValidationRule[];
  accountingTs: IValidationRule[];
  customerId: IValidationRule[];
  identifier: IValidationRule[];
  rideId: IValidationRule[];
  expenseTypeId: IValidationRule[];
  vehicleId: IValidationRule[];
  note: IValidationRule[];
};

type IValidationRule = {
  param: string;
  value: boolean | number[] | string | null;
  message: string | null;
};

export default function EditForm(props: ModalFormProps): JSX.Element {
  usePageWithForm();
  const [isLoaderActive, setIsLoaderActive] = React.useState<boolean>(false);
  const [category, setCategory] = React.useState('');
  const { formatMessage: f } = useIntl();
  const [validation, setValidation] = React.useState<FormValidation | null>(null);
  const [selectOptions, setSelectOptions] = React.useState<{ [key: string]: IOption[] }>({
    vehicleId: [],
    rideId: [],
    expenseTypeId: [],
  });

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    watch,
    formState: { errors },
  } = useForm<EditFormValues>({
    defaultValues: {
      status: '',
      errors: '',
      accountingTs: '',
      customerId: '',
      identifier: '',
      vehicleId: '',
      note: '',
      rideId: '',
      expenseTypeId: '',
    },
    resolver: async (data) => {
      const validateErros = validateFields(data, validation);
      const formErrors = transformErrors(validateErros);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
    context: { validation },
  });

  // save
  const handleUpdate = async (updateValues) => {
    const newValues = { ...updateValues, customerId: props.customerId };
    if (category === IS_REFUELING) {
      delete newValues.expenseTypeId;
    } else {
      delete newValues.rideId;
    }
    setIsLoaderActive(true);

    const response = await ApiService.put(`/v1/expense/import/record/${props.id}/edit`, newValues);
    setIsLoaderActive(false);
    if (response.data && response.data.status === RESPONSE_OK) {
      props.handleClose();
      props.updateTable();
    }
  };

  const handleFormValidation = (data: IFilesImportInput[], type: string): FormValidation => {
    return {
      status: data.find((i) => i.indexId === 'status')?.[type] || [],
      errors: data.find((i) => i.indexId === 'errors')?.[type] || [],
      accountingTs: data.find((i) => i.indexId === 'accountingTs')?.[type] || [],
      customerId: data.find((i) => i.indexId === 'customerId')?.[type] || [],
      identifier: data.find((i) => i.indexId === 'identifier')?.[type] || [],
      rideId: data.find((i) => i.indexId === 'rideId')?.[type] || [],
      expenseTypeId: data.find((i) => i.indexId === 'expenseTypeId')?.[type] || [],
      vehicleId: data.find((i) => i.indexId === 'vehicleId')?.[type] || [],
      note: data.find((i) => i.indexId === 'note')?.[type] || [],
    };
  };

  const handleFormData = (data: IFilesImportInput[], type: string) => {
    return {
      status: data.find((i) => i.indexId === 'status')?.value,
      errors: data.find((i) => i.indexId === 'errors')?.value,
      accountingTs: data.find((i) => i.indexId === 'accountingTs')?.value,
      customerId: data.find((i) => i.indexId === 'customerId')?.value,
      identifier: data.find((i) => i.indexId === 'identifier')?.value,
      rideId: data.find((i) => i.indexId === 'rideId')?.values,
      expenseTypeId: data.find((i) => i.indexId === 'expenseTypeId')?.value,
      vehicleId: data.find((i) => i.indexId === 'vehicleId')?.value,
      note: data.find((i) => i.indexId === 'note')?.value,
    };
  };

  const handleCategory = (data: IFilesImportInput[]) => {
    setCategory(data.find((i) => i.indexId === 'rideId') ? IS_REFUELING : IS_EXPENSE);
  };

  const convertDateValue = (item: string) => {
    let newItem = '';
    const regexFindDate = /[0-9]{4}-[0-9]{2}-[0-9]{2} [0-9]{2}:[0-9]{2}:[0-9]{2}/gi;
    if (regexFindDate.test(item)) {
      const [date, ...info] = item.split(' - ');
      if (date) {
        const dateAfterSplit = date.split(' ');
        if (dateAfterSplit && dateAfterSplit.length === 2) {
          const newDateString = `${dateAfterSplit[0]}T${dateAfterSplit[1]}Z`;
          const regexFindDateString = /[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}Z/gi;
          if (regexFindDateString.test(newDateString)) {
            const formattedDate = dayjs(newDateString).format('DD.MM.YYYY HH:mm');
            newItem = info ? `${formattedDate} - ${info.join(' - ')}` : formattedDate;
          }
        }
      }
    }
    return newItem || item;
  };

  const convertDateOptions = (data: IOption[] | undefined) => {
    if (data) {
      return data.map((i) => {
        i.item = convertDateValue(i.item);
        return i;
      });
    }
    return [];
  };

  const handleOptionsData = (data: IFilesImportInput[]) => {
    return {
      vehicleId: data.find((i) => i.indexId === 'vehicleId')?.values || [],
      rideId: convertDateOptions(data.find((i) => i.indexId === 'rideId')?.values) || [],
      expenseTypeId: data.find((i) => i.indexId === 'expenseTypeId')?.values || [],
    };
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const resendDynamicForm = React.useCallback(async (dynamicFormValues) => {
    setIsLoaderActive(true);
    const response = await TableService.resendDynamicFormById(
      TABLE_REFUELINGS_IMPORT_RECORDS,
      props.id,
      dynamicFormValues,
      props.customerId
    );
    if (response && response.form && response.form.inputs) {
      Object.keys(handleFormData(response.form.inputs, 'value')).map((key) => {
        setValue(key as FormType, handleFormData(response.form.inputs, 'value')[key]);
      });
      setValidation(handleFormValidation(response.form.inputs, 'rules'));
      setSelectOptions(handleOptionsData(response.form.inputs));
    }
    setIsLoaderActive(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (props.data) {
      handleCategory(props.data);
      Object.keys(handleFormData(props.data, 'value')).map((key) => {
        setValue(key as FormType, handleFormData(props.data, 'value')[key]);
      });
      setValidation(handleFormValidation(props.data, 'rules'));
      setSelectOptions(handleOptionsData(props.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAllowed = PermissionsService.hasAccess(
    PERMISSION_IMPORTED_RECORD,
    ['update'],
    props.customerId
  );

  const closeButton: CmdModalFooterButton = {
    id: 'modalClose',
    type: 'button',
    title: f({ id: 'form.close' }),
    className: 'e-button',
    closeCallback: props.handleClose,
    tooltip: f({ id: 'alarms.modal.timeFrame.button.close.tooltip' }),
  };

  const buttons: CmdModalFooterButton[] = isAllowed
    ? [
        { ...closeButton },
        {
          id: 'submitEditForm',
          type: 'submit',
          title: f({ id: 'form.save' }),
          className: 'e-button e-button--gray',
          submitCallback: handleSubmit(handleUpdate),
        },
      ]
    : [{ ...closeButton }];

  const handleVehicle = (vehicleId: string | number) => {
    setValue('vehicleId', (vehicleId as string) || '');
    if (category === IS_REFUELING) {
      setValue('rideId', '');
    } else {
      setValue('expenseTypeId', '');
    }

    resendDynamicForm(getValues());
    // }
  };

  return (
    <CmdModal
      id="files-import-edit-form"
      title={f({ id: 'table.detail' })}
      // {...formikProps}
      footerButtons={buttons}
      loader={isLoaderActive}
    >
      {isAllowed ? (
        <div>
          <CmdLabel
            name="status"
            title={f({ id: 'form.labels.refuelingImportRecords.status' })}
            value={watch('status')}
          />
          <CmdLabel
            title={f({ id: 'form.labels.expenseImportRecords.error' })}
            value={watch('errors')}
          />
          <CmdLabel
            title={f({ id: 'form.labels.expenseImportRecords.accountingTs' })}
            value={dayjs(watch('accountingTs')).format('DD.MM.YYYY HH:mm')}
          />
          <CmdLabel title={f({ id: 'form.labels.customer' })} value={getValues().customerId} />
          <CmdLabel
            title={f({ id: 'form.labels.expenseImportRecords.identifier' })}
            value={watch('identifier')}
          />
          <Controller
            name="vehicleId"
            control={control}
            render={({ field }) => (
              <CmdSimpleSelect
                {...field}
                options={selectOptions.vehicleId || []}
                id="vehicleId"
                label={f({ id: 'form.inputs.vehicle' })}
                introOption={f({ id: 'form.choose' })}
                required={true}
                onChangeCallback={handleVehicle}
                touched={!!errors.vehicleId}
                error={errors.vehicleId?.message}
              />
            )}
          />
          {category === IS_REFUELING ? (
            <Controller
              name="rideId"
              control={control}
              render={({ field }) => (
                <CmdSimpleSelect
                  {...field}
                  options={selectOptions.rideId || []}
                  // name="rideId"
                  id="rideId"
                  label={f({ id: 'form.inputs.refuelingImportRecords.assignedRide' })}
                  introOption={
                    watch('vehicleId')
                      ? f({ id: 'form.choose' })
                      : f({ id: 'form.placeholders.selectVehicleFirst' })
                  }
                  disabled={!watch('vehicleId')}
                  required={true}
                  onChangeCallback={(rideId: string | number) => {
                    setValue('rideId', rideId as string);
                  }}
                  touched={!!errors.rideId}
                  error={errors.rideId?.message}
                />
              )}
            />
          ) : (
            <Controller
              name="expenseTypeId"
              control={control}
              render={({ field }) => (
                <CmdSimpleSelect
                  {...field}
                  options={selectOptions.expenseTypeId || []}
                  // name="expenseTypeId"
                  id="expenseTypeId"
                  label={f({ id: 'form.inputs.expenseType' })}
                  introOption={
                    watch('vehicleId')
                      ? f({ id: 'form.choose' })
                      : f({ id: 'form.placeholders.selectVehicleFirst' })
                  }
                  onChangeCallback={(expenseTypeId: string | number) => {
                    setValue('expenseTypeId', expenseTypeId as string);
                  }}
                  disabled={!watch('vehicleId')}
                  required={true}
                  touched={!!errors.expenseTypeId}
                  error={errors.expenseTypeId?.message}
                />
              )}
            />
          )}
          <Controller
            name="note"
            control={control}
            render={({ field }) => (
              <CmdTextarea
                {...field}
                // name="note"
                id="note"
                label={f({ id: 'form.inputs.note' })}
                rows={5}
                setValue={(value) => setValue('note', value)}
              />
            )}
          />
        </div>
      ) : (
        <div>
          <CmdLabel
            title={f({ id: 'form.labels.refuelingImportRecords.status' })}
            value={watch('status')}
            areLabels
          />
          <CmdLabel
            title={f({ id: 'form.labels.expenseImportRecords.error' })}
            value={watch('errors')}
            areLabels
          />
          <CmdLabel
            title={f({ id: 'form.labels.expenseImportRecords.accountingTs' })}
            value={dayjs(watch('accountingTs')).format('DD.MM.YYYY HH:mm')}
            areLabels
          />
          <CmdLabel
            title={f({ id: 'form.labels.customer' })}
            value={watch('customerId')}
            areLabels
          />
          <CmdLabel
            title={f({ id: 'form.labels.expenseImportRecords.identifier' })}
            value={watch('identifier')}
            areLabels
          />
          <CmdLabel title={f({ id: 'form.inputs.vehicle' })} value={watch('vehicleId')} areLabels />
          {category === IS_REFUELING ? (
            <CmdLabel
              title={f({ id: 'form.inputs.refuelingImportRecords.assignedRide' })}
              value={watch('rideId')}
            />
          ) : (
            <CmdLabel title={f({ id: 'form.inputs.expenseType' })} value={watch('expenseTypeId')} />
          )}
          <CmdLabel title={f({ id: 'form.inputs.note' })} value={watch('note')} areLabels />
        </div>
      )}
    </CmdModal>
  );
}
